// Aktivitäten-Listen (Runde 5, Agent 5C): Typen und Client nach crm/API.md, Abschnitt 20.4.
import { http } from "./client";
import type { Liste, ListenParameter } from "./types";

export type AktivitaetenArt = "anrufe" | "emails" | "sms" | "whatsapp" | "meetings" | "notizen" | "custom";

export interface AktivitaetenArtInfo {
  art: Exclude<AktivitaetenArt, "custom">;
  label: string;
  anzahl: number | null;
}

export interface CustomTypInfo {
  id: string;
  name: string;
  anzahl: number | null;
  /** archivierte Typen stehen nur, solange es noch Einträge gibt */
  archiviert?: boolean;
}

export interface AktivitaetZeile {
  id: string;
  art: AktivitaetenArt;
  lead_id: string | null;
  lead_name: string | null;
  contact_id: string | null;
  contact_name: string | null;
  user_id: string | null;
  user_name: string | null;
  activity_at: string | null;
  date_updated: string | null;
  /** Überschrift der Zeile, z. B. "Called Max Muster", Betreff oder Name der Custom Activity */
  titel: string | null;
  /** Notiz, Mail- oder SMS-Text (gekürzt) */
  text: string | null;
  /** Schlüssel je Art: API.md 20.4 (Typen `AnrufDetails`, `MailDetails`, … unten) */
  details: Record<string, unknown>;
}

export type Richtung = "ausgehend" | "eingehend";

export interface AnrufDetails {
  richtung: Richtung | null;
  /** Close-Ergebnis: answered, no-answer, busy, vm-answer, vm-left, blocked, error, abandoned */
  ergebnis: string | null;
  status: string | null;
  /** Sekunden */
  dauer: number | null;
  telefon: string | null;
  telefon_formatiert: string | null;
  outcome_id: string | null;
  quelle: "close" | "crm";
  /** false = Anrufversuch der CRM-Telefonie, der nie bis zum Kunden kam und nicht in Close steht */
  in_close: boolean;
  fehler: string | null;
}

export interface MailDetails {
  richtung: Richtung | null;
  status: string | null;
  von: string | null;
  an: string[];
  cc: string[];
  anhaenge: number;
  thread_id: string | null;
}

export interface NachrichtDetails {
  richtung: Richtung | null;
  status: string | null;
  telefon: string | null;
  telefon_formatiert: string | null;
}

export interface MeetingDetails {
  beginn: string | null;
  ende: string | null;
  status: string | null;
  ort: string | null;
  teilnehmer: number;
}

export interface NotizDetails {
  angeheftet: boolean;
}

export interface CustomDetails {
  typ_id: string | null;
  typ_name: string | null;
  status: "published" | "draft" | string | null;
  /** {cf_…: Wert}, die Namen und Arten der Felder stehen in den Stammdaten (`meta.typ(typ_id).fields`) */
  felder: Record<string, unknown>;
  quelle: "close" | "crm";
}

export type AktivitaetenSortierung = "activity_at" | "-activity_at" | "date_updated" | "-date_updated" | "lead" | "-lead" | "user" | "-user";

export interface AktivitaetenParameter extends ListenParameter {
  art?: AktivitaetenArt;
  /** nur bei art=custom */
  typ_id?: string;
  user_id?: string;
  lead_id?: string;
  kontakt_id?: string;
  /** einschließlich, Datum (Berliner Tag) oder ISO-Zeitpunkt */
  von?: string;
  /** ausschließlich */
  bis?: string;
  q?: string;
  /** Anrufe, E-Mails, SMS, WhatsApp */
  richtung?: Richtung;
  /** nur Anrufe, mehrere möglich */
  ergebnis?: string[];
  sort?: AktivitaetenSortierung;
}

export interface AktivitaetSuchTreffer {
  id: string;
  art: AktivitaetenArt;
  lead_id: string | null;
  lead_name: string | null;
  titel: string | null;
  ausschnitt: string | null;
  activity_at: string | null;
}

export const aktivitaetenApi = {
  arten: () => http.get<{ data: AktivitaetenArtInfo[]; custom: CustomTypInfo[] }>("/api/aktivitaeten/arten"),
  liste: (p: AktivitaetenParameter = {}, signal?: AbortSignal) =>
    http.get<Liste<AktivitaetZeile>>(
      "/api/aktivitaeten",
      {
        limit: p.limit,
        cursor: p.cursor,
        art: p.art,
        typ_id: p.typ_id,
        user_id: p.user_id,
        lead_id: p.lead_id,
        kontakt_id: p.kontakt_id,
        von: p.von,
        bis: p.bis,
        q: p.q,
        richtung: p.richtung,
        ergebnis: p.ergebnis,
        sort: p.sort,
      },
      signal,
    ),
  /** Suche in Aktivitäten für die Befehlspalette (Gruppe "Aktivitäten") */
  suche: (q: string, limit = 5, signal?: AbortSignal) => http.get<{ data: AktivitaetSuchTreffer[] }>("/api/aktivitaeten/suche", { q, limit }, signal),
};
